import { z } from "zod"

/**
 * Документы о соответствии (решение после встречи 17.10.2026, логика 31.10.2026).
 *
 * Серийную продукцию без документа о соответствии площадка не продаёт: штрафы
 * за это ложатся и на неё. Товар под заказ и по индивидуальным меркам документа
 * не требует; гравировка и персонализация серийного изделия от документа не
 * освобождают (документ нужен на основу); детские товары - без исключений.
 *
 * Регистрацию продавца это не блокирует, сохранение карточки тоже: карточка
 * без документа уходит на модерацию с подсказкой «Остался один шаг», а
 * опубликовать её консоль не даёт, пока документ не подтверждён модератором
 * (или площадка не дала ручной допуск на партию).
 *
 * Продавец загружает документ один раз в библиотеку (Профиль → «Сертификаты и
 * декларации») и привязывает его к любому числу товаров (партия, линейка).
 * Какой документ нужен в категории, решает площадка на узле дерева категорий
 * (наследуется вниз по ветке); рекомендованная матрица с основаниями
 * засеяна миграцией 570.
 */

/** Вид документа. */
export const ComplianceDocumentKindSchema = z.enum([
  "certificate", // сертификат соответствия (ЕАЭС или национальный)
  "declaration", // декларация о соответствии (ЕАЭС или национальная)
  "refusal_letter", // отказное письмо: продукция не подлежит подтверждению
  "state_registration", // свидетельство о государственной регистрации (СГР)
])
export type ComplianceDocumentKind = z.infer<typeof ComplianceDocumentKindSchema>
export const COMPLIANCE_DOCUMENT_KINDS = ComplianceDocumentKindSchema.options

/**
 * Решение площадки по документу:
 * - `pending` - загружен продавцом, ждёт проверки модератором;
 * - `approved` - модератор сверил номер с реестром и подтвердил;
 * - `rejected` - отклонён с причиной.
 * До 31.10.2026 был один статус `active` (без проверки) - миграция 570
 * перевела такие документы в `pending`.
 */
export const ComplianceDocumentStatusSchema = z.enum(["pending", "approved", "rejected"])
export type ComplianceDocumentStatus = z.infer<typeof ComplianceDocumentStatusSchema>

/**
 * Узлы дерева, где документ рекомендуется включить кнопкой «Включить
 * рекомендованные». С 31.10.2026 рекомендация живёт в базе (матрица
 * миграции 570, поля cert_rec_*), список оставлен для старых клиентов.
 */
export const COMPLIANCE_RECOMMENDED_CATEGORY_IDS = [
  "cat-w-clothing",
  "cat-m-clothing",
  "cat-w-shoes",
  "cat-m-shoes",
  "cat-4",
  "cat-5",
  "cat-7-2",
] as const

/** За сколько дней до окончания срока продавец получает напоминания. */
export const COMPLIANCE_EXPIRY_NOTICE_STEPS = [30, 7, 1] as const
/** Первое напоминание - для плашек «срок выходит». */
export const COMPLIANCE_EXPIRY_NOTICE_DAYS = 30

/**
 * Страница проверки в реестре. Реестр Росаккредитации не даёт прямой ссылки
 * на запись по номеру (запись открывается по внутреннему id), поэтому ссылка
 * ведёт в поиск нужного реестра, а номер показывается рядом. Свою прямую
 * ссылку на запись продавец может указать в карточке документа - тогда она
 * важнее этой. СГР - реестр свидетельств Роспотребнадзора.
 */
export const complianceRegistrySearchUrl = (kind: ComplianceDocumentKind): string | null => {
  if (kind === "certificate") return "https://pub.fsa.gov.ru/rss/certificate"
  if (kind === "declaration") return "https://pub.fsa.gov.ru/rds/declaration"
  if (kind === "state_registration") return "https://fp.crc.ru/"
  return null
}

// ── Проверка формата номера ──────────────────────────────────────────────

/**
 * Номер приводится к одному виду: верхний регистр, «№» → «N», латинские
 * буквы-двойники → кириллица там, где реестр пишет кириллицу (С, Д, Е),
 * лишние пробелы убраны. Продавцы набирают номер то латиницей, то
 * кириллицей - это не ошибка, а одна и та же запись.
 */
const LATIN_TO_CYR: Record<string, string> = {
  A: "А",
  B: "В",
  C: "С",
  E: "Е",
  H: "Н",
  K: "К",
  M: "М",
  O: "О",
  P: "Р",
  T: "Т",
  X: "Х",
  Y: "У",
}
const CYR_TO_LATIN: Record<string, string> = Object.fromEntries(
  Object.entries(LATIN_TO_CYR).map(([latin, cyr]) => [cyr, latin]),
)

/** Всё в латиницу, где у буквы есть двойник: так сравнивать проще. */
const toLatinShape = (value: string): string =>
  value
    .toUpperCase()
    .replace(/№/g, "N")
    .replace(/\s+/g, " ")
    .trim()
    .split("")
    .map((ch) => CYR_TO_LATIN[ch] ?? ch)
    .join("")

export type ComplianceNumberFormat =
  | "eaeu_certificate" // ЕАЭС RU С-RU.АБ12.В.01234/23
  | "eaeu_declaration" // ЕАЭС N RU Д-RU.РА01.В.12345/23
  | "ts_certificate" // ТС RU С-RU.АЯ46.В.12345 (выданы до 2021 года)
  | "ts_declaration" // ТС N RU Д-RU.АУ04.В.12345
  | "national_certificate" // РОСС RU С-RU.АБ12.В.01234/23
  | "national_declaration" // РОСС RU Д-RU.РА01.В.12345/23, RU Д-RU.РА01.В.12345/23
  | "state_registration" // RU.77.01.34.001.Е.000123.01.21

export type ComplianceNumberCheck = {
  /** Номер похож на номер реестра для этого вида документа. */
  ok: boolean
  format: ComplianceNumberFormat | null
}

// Код органа / заявителя: латиница, кириллица и цифры (АЯ46, РА01, HB61).
const BODY = "[A-ZА-ЯЁ0-9]{2,6}"
const SCHEME = "[A-ZА-ЯЁ]"
const SERIAL = "\\d{5,6}(?:\\/\\d{2})?"
const COUNTRY = "(?:RU|BY|KZ|AM|KG)"
// Страна заявителя / изготовителя после «С-» / «Д-»: любая (CN, TR, IT…).
const APPLICANT = "[A-Z]{2}"
// После toLatinShape: «ЕАЭС» → «EAЭC», «С-» → «C-», «ТС» → «TC», «РОСС» → «POCC».
const FORMATS: { format: ComplianceNumberFormat; kind: ComplianceDocumentKind; re: RegExp }[] = [
  {
    format: "eaeu_certificate",
    kind: "certificate",
    re: new RegExp(`^EAЭC ${COUNTRY} C-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  {
    format: "eaeu_declaration",
    kind: "declaration",
    re: new RegExp(`^EAЭC N ${COUNTRY} Д-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  {
    format: "ts_certificate",
    kind: "certificate",
    re: new RegExp(`^TC ${COUNTRY} C-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  {
    format: "ts_declaration",
    kind: "declaration",
    re: new RegExp(`^TC N ${COUNTRY} Д-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  {
    format: "national_certificate",
    kind: "certificate",
    re: new RegExp(`^POCC ${COUNTRY} C-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  // Сертификат ГОСТ Р старого образца: РОСС RU.АЯ46.Н12345.
  {
    format: "national_certificate",
    kind: "certificate",
    re: new RegExp(`^POCC RU\\.${BODY}\\.${SCHEME}\\d{5}$`),
  },
  {
    format: "national_declaration",
    kind: "declaration",
    re: new RegExp(`^(?:POCC )?${COUNTRY} Д-${APPLICANT}\\.${BODY}\\.${SCHEME}\\.${SERIAL}$`),
  },
  {
    format: "state_registration",
    kind: "state_registration",
    re: new RegExp(
      `^${COUNTRY}\\.\\d{2}\\.\\d{2}\\.\\d{2}\\.\\d{2,3}\\.${SCHEME}\\.\\d{6}\\.\\d{2}\\.\\d{2}$`,
    ),
  },
]

/**
 * Формат номера: ЕАЭС, ТС (старые), национальный (ПП РФ № 2425), СГР.
 * Отказное письмо номера реестра не имеет - любой непустой номер годится.
 * Проверка формата не заменяет сверку с реестром: она ловит опечатки и
 * «номер» вида «есть», а запись в реестре модератор открывает по ссылке.
 */
export const checkComplianceNumber = (
  kind: ComplianceDocumentKind,
  number: string,
): ComplianceNumberCheck => {
  const shape = toLatinShape(number)
  if (!shape) return { ok: false, format: null }
  if (kind === "refusal_letter") return { ok: true, format: null }
  const hit = FORMATS.find((entry) => entry.re.test(shape))
  if (!hit) return { ok: false, format: null }
  return { ok: hit.kind === kind, format: hit.format }
}

export const ComplianceNumberCheckSchema = z.object({
  ok: z.boolean(),
  format: z
    .enum([
      "eaeu_certificate",
      "eaeu_declaration",
      "ts_certificate",
      "ts_declaration",
      "national_certificate",
      "national_declaration",
      "state_registration",
    ])
    .nullable(),
})

// ── Документ ─────────────────────────────────────────────────────────────

export const ComplianceDocumentFileSchema = z.object({
  name: z.string(),
  mime: z.string(),
  sizeBytes: z.number(),
})

/** Документ библиотеки продавца (кабинет и консоль). */
export const ComplianceDocumentSchema = z.object({
  id: z.string(),
  shopId: z.string(),
  /** Название магазина - консоли (очередь проверки). */
  shopName: z.string().nullable().optional(),
  kind: ComplianceDocumentKindSchema,
  number: z.string(),
  /** ГГГГ-ММ-ДД. */
  issuedAt: z.string().nullable(),
  /** ГГГГ-ММ-ДД, последний день действия; null - бессрочный. */
  validUntil: z.string().nullable(),
  /** На что выдан: наименование продукции. */
  productName: z.string(),
  /** Бренд / изготовитель. */
  brand: z.string(),
  /** Прямая ссылка на запись в реестре, если продавец её указал. */
  registryUrl: z.string().nullable(),
  /** Куда вести «Проверить в реестре»: своя ссылка продавца или поиск реестра. */
  checkUrl: z.string().nullable(),
  status: ComplianceDocumentStatusSchema,
  rejectReason: z.string().nullable(),
  /** Комментарий модератора при подтверждении. */
  reviewComment: z.string().nullable().optional(),
  /** Когда модератор принял решение (ISO). */
  reviewedAt: z.string().nullable().optional(),
  /** Формат номера похож на номер реестра. */
  numberCheck: ComplianceNumberCheckSchema.optional(),
  /** Срок вышел (на сегодня по Москве). */
  expired: z.boolean(),
  /** До окончания срока не больше COMPLIANCE_EXPIRY_NOTICE_DAYS дней. */
  expiresSoon: z.boolean(),
  file: ComplianceDocumentFileSchema.nullable(),
  /** Сколько товаров привязано (без корзины). */
  productCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ComplianceDocument = z.infer<typeof ComplianceDocumentSchema>

/** Поля документа, которые задаёт продавец. Файл - отдельной частью формы. */
export const ComplianceDocumentInputSchema = z.object({
  kind: ComplianceDocumentKindSchema,
  number: z.string().trim().min(1).max(120),
  issuedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  productName: z.string().trim().min(1).max(300),
  brand: z.string().trim().max(200).optional(),
  registryUrl: z.string().trim().max(1000).nullable().optional(),
})
export type ComplianceDocumentInput = z.infer<typeof ComplianceDocumentInputSchema>

/** Решение модератора «Подтвердить»: срок можно поправить по реестру. */
export const ComplianceApproveInputSchema = z
  .object({
    validUntil: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
    comment: z.string().trim().max(1000).optional(),
  })
  .strict()
export type ComplianceApproveInput = z.infer<typeof ComplianceApproveInputSchema>

// ── Ручной допуск площадкой ─────────────────────────────────────────────

/**
 * «Сертификат на партию подтверждён площадкой»: модератор видел документ вне
 * системы (почта, встреча) и разрешает публикацию товара до даты. Комментарий
 * и срок обязательны; вид и номер документа - если известны, их увидит
 * покупатель.
 */
export const ComplianceAdmissionInputSchema = z
  .object({
    until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    comment: z.string().trim().min(1).max(1000),
    kind: ComplianceDocumentKindSchema.nullable().optional(),
    number: z.string().trim().max(120).nullable().optional(),
  })
  .strict()
export type ComplianceAdmissionInput = z.infer<typeof ComplianceAdmissionInputSchema>

export const ComplianceAdmissionSchema = z.object({
  /** ГГГГ-ММ-ДД, последний день допуска. */
  until: z.string(),
  comment: z.string(),
  kind: ComplianceDocumentKindSchema.nullable(),
  number: z.string().nullable(),
  byName: z.string().nullable(),
  at: z.string().nullable(),
  /** Срок допуска вышел. */
  expired: z.boolean(),
})
export type ComplianceAdmission = z.infer<typeof ComplianceAdmissionSchema>

// ── Состояние товара ─────────────────────────────────────────────────────

/**
 * Состояние товара по документу:
 * - `not_required` - категория документ не требует;
 * - `exempt` - требует, но товар под заказ / по индивидуальным меркам;
 * - `ok` - привязан подтверждённый действующий документ;
 * - `admitted` - ручной допуск площадкой до даты;
 * - `pending` - документ привязан, ждёт проверки модератором;
 * - `missing` - документа нет («Остался один шаг»);
 * - `expired` - привязанный документ истёк;
 * - `rejected` - привязанный документ отклонён модератором.
 */
export const ProductComplianceStateSchema = z.enum([
  "not_required",
  "exempt",
  "ok",
  "admitted",
  "pending",
  "missing",
  "expired",
  "rejected",
])
export type ProductComplianceState = z.infer<typeof ProductComplianceStateSchema>

/** Почему площадка сняла товар с продажи: истёк документ, его не было, его отклонили. */
export const ProductComplianceHoldSchema = z.enum(["expired", "missing", "rejected"])
export type ProductComplianceHold = z.infer<typeof ProductComplianceHoldSchema>

/** Требование категории (с наследованием) - форме товара и консоли. */
export const CategoryComplianceSchema = z.object({
  required: z.boolean(),
  kinds: z.array(ComplianceDocumentKindSchema),
  /** Узел, от которого пришло значение; null - нигде не задано. */
  sourceCategoryId: z.string().nullable(),
  /** Основание: техрегламент (например «ТР ТС 017/2011»). */
  basis: z.string().nullable().optional(),
  /** Пояснение продавцу: что именно нужно в этой группе. */
  note: z.string().nullable().optional(),
  /** Маркировка «Честный знак» в группе (справочно, площадка её не проверяет). */
  marking: z.string().nullable().optional(),
  /** Без исключений «под заказ» (детские товары). */
  strict: z.boolean().optional(),
})
export type CategoryCompliance = z.infer<typeof CategoryComplianceSchema>

/** Документ и состояние товара - кабинету продавца и консоли. */
export const ProductComplianceSchema = z.object({
  /** Категория требует документ (с наследованием по дереву). */
  required: z.boolean(),
  /** Товар под заказ / по индивидуальным меркам - документ не нужен. */
  exempt: z.boolean(),
  state: ProductComplianceStateSchema,
  /** Допустимые виды документа; пусто - любой. */
  allowedKinds: z.array(ComplianceDocumentKindSchema),
  /** Опубликовать нельзя (state = missing/pending/expired/rejected). */
  blocksPublication: z.boolean(),
  hold: ProductComplianceHoldSchema.nullable(),
  document: ComplianceDocumentSchema.nullable(),
  /** Продавец отметил «персонализация серийного товара» - документ нужен на основу. */
  serialBase: z.boolean().optional(),
  /** Ручной допуск площадкой (действующий или истёкший). */
  admission: ComplianceAdmissionSchema.nullable().optional(),
  /** Требование категории с основанием - для подсказки. */
  category: CategoryComplianceSchema.optional(),
})
export type ProductCompliance = z.infer<typeof ProductComplianceSchema>

/** Что видит покупатель на странице товара: без файла и служебных полей. */
export const PublicComplianceDocumentSchema = z.object({
  kind: ComplianceDocumentKindSchema,
  number: z.string(),
  validUntil: z.string().nullable(),
  checkUrl: z.string().nullable(),
})
export type PublicComplianceDocument = z.infer<typeof PublicComplianceDocumentSchema>

/** Сводка для плашки в кабинете продавца. */
export const SellerComplianceSummarySchema = z.object({
  /** Опубликованы, хотя категория требует документ, а его нет (или отклонён/истёк). */
  publishedMissing: z.number().int().nonnegative(),
  /** Карточки (кроме корзины), которым нужно действие продавца: документа нет, он отклонён или истёк. */
  missing: z.number().int().nonnegative(),
  /** Документ привязан и ждёт проверки площадкой. */
  pending: z.number().int().nonnegative().optional(),
  /** Сняты площадкой из-за документа. */
  held: z.number().int().nonnegative(),
  /** Документы, срок которых выходит в ближайшие 30 дней. */
  expiringDocuments: z.number().int().nonnegative(),
})
export type SellerComplianceSummary = z.infer<typeof SellerComplianceSummarySchema>

// ── Матрица категорий (консоль) ──────────────────────────────────────────

/** Значения узла: свои (null - как у родителя) или рекомендованные матрицей. */
export const CategoryComplianceValuesSchema = z.object({
  required: z.boolean().nullable(),
  kinds: z.array(ComplianceDocumentKindSchema).nullable(),
  basis: z.string().nullable(),
  note: z.string().nullable(),
  marking: z.string().nullable(),
  strict: z.boolean().nullable(),
})
export type CategoryComplianceValues = z.infer<typeof CategoryComplianceValuesSchema>

export const ComplianceMatrixRowSchema = z.object({
  categoryId: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  archived: z.boolean(),
  own: CategoryComplianceValuesSchema,
  recommended: CategoryComplianceValuesSchema,
  resolved: CategoryComplianceSchema,
  /** Своё значение флажка расходится с рекомендацией (или ещё не задано). */
  differsFromRecommended: z.boolean(),
})
export type ComplianceMatrixRow = z.infer<typeof ComplianceMatrixRowSchema>

/** Правка узла в консоли. Поле не передано - не менять; null - как у родителя. */
export const CategoryComplianceInputSchema = z
  .object({
    required: z.boolean().nullable(),
    kinds: z.array(ComplianceDocumentKindSchema).nullable().optional(),
    basis: z.string().trim().max(300).nullable().optional(),
    note: z.string().trim().max(600).nullable().optional(),
    marking: z.string().trim().max(300).nullable().optional(),
    strict: z.boolean().nullable().optional(),
  })
  .strict()
export type CategoryComplianceInput = z.infer<typeof CategoryComplianceInputSchema>

// ── Отчёт консоли ────────────────────────────────────────────────────────

/** Строка отчёта «Товары без документа в обязательных категориях». */
export const ComplianceReportItemSchema = z.object({
  productId: z.string(),
  name: z.string(),
  article: z.string(),
  status: z.string(),
  shopId: z.string(),
  shopName: z.string().nullable(),
  categoryId: z.string().nullable(),
  category: z.string().nullable(),
  /** missing / pending / expired / rejected. */
  state: ProductComplianceStateSchema,
  documentNumber: z.string().nullable(),
  updatedAt: z.string(),
})
export type ComplianceReportItem = z.infer<typeof ComplianceReportItemSchema>

export const ComplianceReportGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  total: z.number().int(),
  published: z.number().int(),
  missing: z.number().int(),
  pending: z.number().int(),
  expired: z.number().int(),
  rejected: z.number().int(),
})
export type ComplianceReportGroup = z.infer<typeof ComplianceReportGroupSchema>

export const ComplianceReportSchema = z.object({
  total: z.number().int(),
  byShop: z.array(ComplianceReportGroupSchema),
  byCategory: z.array(ComplianceReportGroupSchema),
  items: z.array(ComplianceReportItemSchema),
})
export type ComplianceReport = z.infer<typeof ComplianceReportSchema>
